import { NodeApi } from 'platejs';
import z from 'zod';

import createCaptionTitleElement from '@@plate/serialization/creators/createCaptionTitleElement';
import { createElement } from '@@plate/serialization/creators/createElement';

import { Caption, type CaptionElement } from './Caption';
import { CaptionTitle, type CaptionTitleElement } from './CaptionTitle';
import { Credit, type CreditElement } from './Credit';
import { ParagraphCodec, UnityParagraph } from './Paragraph';
import { TextElementsCodec } from './Text';
import { CURRENT_VERSION, ElementId, UnityElementId, Version } from '../commons';
import { ELEMENT_TYPES, MIME_TYPES } from '../constants';

export const UnityYoutube = z.object({
    type: z.literal(ELEMENT_TYPES.YOUTUBE),
    mimetype: z.literal(MIME_TYPES.YOUTUBE),
    elementId: UnityElementId,
    templateElement: z.boolean().optional(),
    version: Version,
    originalSrc: z.string().nullable(),
    src: z.string(),
    contentAttributes: z.object({
        captionTitle: z.string().nullable(),
        caption: z
            .object({
                richTextFormat: z.array(UnityParagraph),
            })
            .nullable(),
        credit: z.string().nullable(),
        startTime: z.number().nullish(),
    }),
});
export type UnityYoutube = z.infer<typeof UnityYoutube>;

export const PlateYoutube = z.object({
    type: z.literal(ELEMENT_TYPES.YOUTUBE),
    id: ElementId,
    data: z.object({
        mimetype: z.literal(MIME_TYPES.YOUTUBE),
        templateElement: z.boolean().optional(),
        originalSrc: z.string().nullable(),
        src: z.string(),
        embed: z.object({
            startTime: z.number().nullish(),
        }),
    }),
    children: z.tuple([CaptionTitle, Caption, Credit]),
});
export type YoutubeElement = z.infer<typeof PlateYoutube>;

export const YoutubeCodec = z.codec(UnityYoutube, PlateYoutube, {
    decode: (value) => {
        const captionTitle = createCaptionTitleElement(value.contentAttributes.captionTitle);
        const captionItems = value.contentAttributes.caption?.richTextFormat[0]?.items;
        const caption: CaptionElement = {
            type: ELEMENT_TYPES.CAPTION,
            id:
                value.contentAttributes.caption?.richTextFormat[0]?.elementId ??
                crypto.randomUUID(),
            ...(value.templateElement && { data: { templateElement: value.templateElement } }),
            children: captionItems ? TextElementsCodec.decode(captionItems) : [{ text: '' }],
        };

        const credit = createElement(ELEMENT_TYPES.CREDIT, {
            children: [{ text: value.contentAttributes.credit ?? '' }],
        });

        return {
            type: value.type,
            id: value.elementId ?? crypto.randomUUID(),
            data: {
                mimetype: value.mimetype,
                originalSrc: value.originalSrc,
                src: value.src,
                embed: {
                    startTime: value.contentAttributes.startTime,
                },
                ...(value.templateElement && { templateElement: value.templateElement }),
            },
            children: [captionTitle, caption, credit] satisfies [
                CaptionTitleElement,
                CaptionElement,
                CreditElement,
            ],
        };
    },
    encode: (value) => {
        const captionTitleElement = NodeApi.string(value.children[0]);

        const captionElement = ParagraphCodec.encode({
            ...value.children[1],
            type: ELEMENT_TYPES.PARAGRAPH,
        });

        const creditElement = NodeApi.string(value.children[2]);

        return {
            type: value.type,
            elementId: value.id,
            version: CURRENT_VERSION,
            contentAttributes: {
                captionTitle: captionTitleElement,
                caption: {
                    richTextFormat: [captionElement],
                },
                credit: creditElement,
                startTime: value.data.embed.startTime,
            },
            ...value.data,
        };
    },
});
